import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesButlerCampaignPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Raffles "The Butler Did It" Global Campaign' }
  ];

  const article = {
    category: 'Industry Trends',
    date: 'September 10, 2024',
    title: 'Raffles Hotels & Resorts Global Brand Campaign "The Butler Did It"',
    subtitle: 'Luxury Hotel Butler Service Innovation',
    heroImage: '/images/generated/jba-fs-105-news_raffles-butler-campaign.webp',
    lead: 'Global luxury hotel brand Raffles Hotels & Resorts announced an innovative global campaign "The Butler Did It" celebrating their legendary butler service.\n\nThis campaign featuring prominent fashion industry creators is attracting attention as a groundbreaking initiative communicating the modern value of butler services to the world.',
    comment: 'Raffles Hotels & Resorts\' "The Butler Did It" campaign is deeply respected by our association as a groundbreaking initiative communicating the true value of butler services to the world.\n\nAs this campaign demonstrates, butler service is not mere luxury service but a specialized profession bringing true value to guests\' lives.\n\nParticularly, the concept of "hyper-personalization" deeply resonates with the "omotenashi spirit" we promote in Japan, proving the universal value of butler services transcending Eastern and Western cultures.\n\nWe expect such international initiatives to significantly contribute to the development of butler culture and improvement of social recognition in Japan, and will make even greater efforts toward providing world-class butler services.',
    related: [
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/jba-fs-109-news_raffles-butler-campaign-v3.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles London "The Butler Did It" Campaign Chapter 2', title: 'Raffles London "The Butler Did It" Campaign Chapter 2', desc: 'Sequel campaign set in London' },
      { to: '/en/news/downton-abbey-grand-finale', image: '/images/generated/jba-fs-110-news_raffles-butler-campaign-v4.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Downton Abbey: The Grand Finale Film Release', title: 'Downton Abbey: The Grand Finale Film Release', desc: 'Global attention as the culmination of butler culture' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'Raffles Hotels & Resorts (Accor Pressroom official announcement)', link: false },
    { label: 'Campaign Announcement', value: 'https://press.accor.com/raffles-the-butler-did-it', link: true },
    { label: 'Official Campaign Site', value: 'https://www.raffles.com/the-butler-did-it', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-overview', label: 'Campaign Overview and Background' },
    { id: 's-talent', label: 'Campaign Talent Spotlight' },
    { id: 's-value', label: 'Modern Value of Butler Services' },
    { id: 's-legacy', label: 'Butler Service Legacy' },
    { id: 's-impact', label: 'Industry Impact and Response' }
  ];

  return (
    <Layout>
      <title>Raffles Hotels & Resorts Global Brand Campaign "The Butler Did It" | Japan Butler Association</title>
      <meta name="description" content="Raffles Hotels & Resorts launches the innovative global campaign The Butler Did It, featuring prominent fashion creators to communicate the modern value of legendary butler service." />
      <link rel="canonical" href="https://butler-association.jp/en/news/raffles-butler-campaign" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/raffles-butler-campaign" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/raffles-butler-campaign" />

      <ArticleHero eyebrow="News / Industry" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'The campaign was announced on September 10, 2024. Set at Raffles Singapore, it was directed by Trey Laird with photography by Dylan Don, featuring Waris Ahluwalia, Robert Rabensteiner and May Siu, with Tim Easton as the Butler',
                'Raffles butler service has been a signature of the brand since the 1887 opening in Singapore, a tradition spanning more than 130 years',
                'The campaign launched globally and simultaneously across print, digital video, digital display and paid social channels'
              ]}
            />

            {/* 01: Overview (existing body, unchanged) */}
            <SectionHeading id="s-overview" en="Overview">Campaign Overview and Background</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Raffles Hotels & Resorts is developing an innovative global campaign "The Butler Did It" that reinterprets their legendary butler service cultivated over 130 years from a modern perspective.</p>
                <p className={P}>This campaign is positioned as an important initiative communicating the cultural value of butler services to the world, beyond mere hotel marketing.</p>
              </div>
              <p className={P}>The campaign production involved renowned creative directors and photographers from the fashion industry, achieving unique expression fusing butler service aesthetics with modern sensibility.</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-butler-campaign-accor.jpg"
              alt="Screenshot of the Accor Pressroom press release announcing the Raffles The Butler Did It campaign, listing the cast and production credits"
              media="Accor Pressroom (Raffles Hotels & Resorts official)"
              url="https://press.accor.com/raffles-the-butler-did-it"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Reinterpreting the legendary butler service cultivated over 130 years from a modern perspective." cite="Raffles &quot;The Butler Did It&quot;" />
            </div>

            {/* Augmented: Campaign Talent */}
            <SectionHeading id="s-talent" en="Campaign Talent">Campaign Talent Spotlight</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Visuals shot by fashion photographer Dylan Don under creative direction from Trey Laird feature designer and actor Waris Ahluwalia and fashion icon Robert Rabensteiner as quintessential Raffles guests, with Tim Easton starring as the Butler.</p>
              <p className={P}>Styling by Rabensteiner adds a distinctive fashion-forward touch against the backdrop of Raffles Singapore. The campaign launched globally and simultaneously across print, digital video, digital display and paid social channels.</p>
            </div>

            {/* 02: Modern Value (existing body, unchanged) */}
            <SectionHeading id="s-value" en="Modern Value">Modern Value of Butler Services</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The campaign portrays Raffles' butler service not as mere luxury service but as the embodiment of "hyper-personalization" that deeply understands each guest's personality and preferences to create experiences exceeding expectations.</p>
              <div className="space-y-5">
                <p className={P}>True luxury sought by modern wealthy individuals is not material opulence but quality of time and experience, with Raffles' butlers positioned as the ultimate embodiment of this.</p>
                <p className={P}>This campaign accurately capturing such value changes has received high praise from within and outside the industry.</p>
              </div>
            </div>

            {/* Augmented: Butler Legacy */}
            <SectionHeading id="s-legacy" en="Butler Legacy">Butler Service Legacy</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Raffles butler service has been a signature since the <Stat>1887</Stat> opening in Singapore, anticipating every guest need with legendary discretion and savoir-faire. A heritage handed down for more than <Stat>130</Stat> years underpins this campaign.</p>
              <p className={P}>The official campaign site highlights butler-designed suite stays including airport transfers, breakfast, champagne and spa treatments as concrete examples of the personalized service.</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-butler-campaign-raffles.jpg"
              alt="Screenshot of the Raffles Hotels & Resorts official website page for the The Butler Did It campaign, showing butler-designed suite stay offers"
              media="Raffles Hotels & Resorts official site"
              url="https://www.raffles.com/the-butler-did-it"
            />

            {/* 03: Industry Impact (existing body, unchanged) */}
            <SectionHeading id="s-impact" en="Industry Impact">Industry Impact and Response</SectionHeading>
            <div className="space-y-6">
              <p className={P}>"The Butler Did It" campaign is having a major impact on the entire hospitality industry, with other luxury hotel brands beginning to reassess the value of butler services and consider similar initiatives.</p>
              <div className="space-y-5">
                <p className={P}>Particularly noteworthy is that this campaign is contributing to improving the social status of the butler profession.</p>
                <p className={P}>Butler training institutions worldwide are seeing increased enrollment applications due to the campaign's influence, with international interest in the butler profession growing.</p>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Announced</dt><dd className="text-[#333] mt-0.5">September 10, 2024</dd></div>
                  <div><dt className="text-[#8f7a56]">Brand</dt><dd className="text-[#333] mt-0.5">Raffles Hotels & Resorts</dd></div>
                  <div><dt className="text-[#8f7a56]">Setting</dt><dd className="text-[#333] mt-0.5">Raffles Singapore</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
